import {useEffect, useRef} from 'react';

import EppoFlags from '@/EppoFlags';
import {useStringAssignmentExperimentDetails} from '@/eppoHooks';
import useMediaQuery from '@/hooks/useMediaQuery';
import {appBarMedia} from '@/styles/appBarMq';

type UseEventBarFastFollowsOptions = {
  /** Viewport floor for exposure. The collapsible FDIC bar shows from lg; every other mount is xl. */
  minAppBarViewport?: 'lg' | 'xl';
  trackExposure?: boolean;
};

/**
 * ezx584::Search::EventBarFastFollows — arm gate plus opt-in exposure.
 *
 * EventBarV2 mounts below its breakpoint (CSS-hidden) and twice over on home and search once the
 * page scrolls, so only AppBar reports — off the same sentinel EventBarRedesign exposure uses.
 */
const useEventBarFastFollows = ({
  minAppBarViewport = 'xl',
  trackExposure = false,
}: UseEventBarFastFollowsOptions = {}) => {
  const {variation, trackExperimentExposed} = useStringAssignmentExperimentDetails(
    EppoFlags.EventBarFastFollows,
  );
  const isWideEnough = useMediaQuery(appBarMedia[minAppBarViewport]);
  const hasTrackedExposure = useRef(false);

  useEffect(() => {
    if (!trackExposure || hasTrackedExposure.current) return;
    if (!isWideEnough || variation === 'fallback') return;

    hasTrackedExposure.current = true;
    trackExperimentExposed();
  }, [trackExposure, isWideEnough, variation, trackExperimentExposed]);

  const showEventBarFastFollows = variation === 'treatment';

  return {showEventBarFastFollows, variation};
};

export default useEventBarFastFollows;
